// Map #6 — Ember Wastes (owner 2026-11-02: 'get as creative as you can be with each'). An original volcanic field at
// night: a violet-grey basalt plateau of cracked slabs, ringed by rivers of lava, with a distant volcano throwing a red-lit
// plume over the horizon. Basalt fangs (spires), column stumps (boulders), obsidian shards (rocks), charred dead trees and
// dry ash-grass (they burn). Three LIGHT, telegraphed hazards that hit the hero and the foes alike:
//   1. lava fissures (burn): cracks split open over the run (6 at the start, 8 more, one a minute) and stay open;
//      3 HP/s on the hero, 4.5 % max HP/s on foes; lure a horde across one.
//   2. eruption volleys (crush + burn): from 1:50 the volcano lobs a volley of 6 lava bombs every 40 s at one patch of the
//      plateau; each lands on a target circle after a 2.6 s+ warning (14 damage, crushes regular foes, leaves a crater)
//      and leaves a magma splash that burns for 5 s.
//   3. sulphur vents (fog): five vents hiss, then blow steam for 9 s: visibility drops inside and everyone is a little slower.
// PURE (check:ip): no three, no DOM, no unseeded randomness. Everything random comes from ctx.rng.
import type { HazardCtx, MapCoreDef } from '../../core/mapdef';
import type { HazardDef } from '../../core/hazards';
import type { Prop, VariantDef } from '../../core/terrain';

/** Charred dead trees (tree archetype: breakable, burns). r = trunk hit radius at s = 1. Render: ember-wastes/props.ts. */
export const DEAD_TREE_VARIANTS: VariantDef[] = [
  { name: 'snag', r: 0.42, s: [0.9, 1.25], h: [0.95, 1.15], hp: 0.8, w: 3 },  // tall bare trunk, a few broken limbs
  { name: 'claw', r: 0.5, s: [0.85, 1.15], h: [0.85, 1.05], hp: 1, w: 2 },    // forked, spreading clawed branches
  { name: 'stump', r: 0.55, s: [0.8, 1.1], h: [0.85, 1.0], hp: 0.7, w: 2 },   // snapped short trunk, jagged top
];
/** Dry ash-grass and ember thornbrush (bush archetype: low, breakable, goes up fast when it burns). */
export const ASH_BUSH_VARIANTS: VariantDef[] = [
  { name: 'tuft', r: 0.95, s: [0.8, 1.2], h: [0.9, 1.1], hp: 1, w: 3 },       // a dry ash-grass tussock
  { name: 'thorn', r: 0.8, s: [0.8, 1.15], h: [0.9, 1.2], hp: 0.9, w: 2 },    // spiky thornbrush with ember tips
];

/** Dry ash-grass patches (where a fire may spread). A seedless trig field, so the sim and the ground shader agree exactly
 *  (render/env.ts draws the same function in TSL). True on roughly a fifth of the plateau. */
export const ASH_GRASS_T = 0.62;
export function ashGrassField(x: number, z: number): number {
  return Math.sin(x * 0.083 + 0.6) * Math.sin(z * 0.071 + 1.9) + 0.55 * Math.sin((x - z) * 0.047 + 0.3) + 0.3 * Math.sin(x * 0.13 + z * 0.11 + 2.2);
}
export const ashGrassAt = (x: number, z: number): boolean => ashGrassField(x, z) > ASH_GRASS_T;

/** Hazard numbers (tests read them). */
export const EMBER = {
  fissure: { first: 6, later: 8, laterStart: 75, laterEvery: 60, power: 3, warn: 2.5, warnLate: 3.5, l: [6, 10.5] as [number, number], w: [0.8, 1.1] as [number, number] },
  volley: { groups: 12, start: 110, gap: 40, bombs: 6, spread: 13, r: 3, warn: 2.6, stagger: 0.28, power: 14, pool: { r: 2.4, power: 4, active: 5 } },
  vent: { n: 5, r: 6, warn: 2.5, active: 9, power: 0.5, start: 20, every: [34, 46] as [number, number] },
  /** nothing lands or opens within this radius of the spawn */
  spawnClear: 14,
} as const;

/** Counts above were authored on the 110 m arena; they scale with the arena area (owner 2026-11-02: 160 m) so the hazard
 *  density stays the same. Volleys keep their 480 s cycle: more groups, each a shorter gap apart. */
export function emberCounts(arenaR: number): { first: number; later: number; groups: number; gap: number; vents: number } {
  const k = Math.max(1, (arenaR / 110) ** 2), groups = Math.round(EMBER.volley.groups * k);
  return { first: Math.round(EMBER.fissure.first * k), later: Math.round(EMBER.fissure.later * k), groups,
    gap: (EMBER.volley.groups * EMBER.volley.gap) / groups, vents: Math.round(EMBER.vent.n * k) };
}

const solidNear = (props: readonly Prop[], x: number, z: number, pad: number) => props.some((p) => p.solid && (p.x - x) ** 2 + (p.z - z) ** 2 < (p.r + pad) ** 2);

function emberHazards({ rng, arenaR, props }: HazardCtx): HazardDef[] {
  const out: HazardDef[] = [];
  const F = EMBER.fissure, V = EMBER.volley, S = EMBER.vent, C = emberCounts(arenaR);
  /** a point in the band [dMin, dMax] from the centre, clear of solid props by `pad` (best effort: 40 tries) */
  const spot = (dMin: number, dMax: number, pad: number): [number, number] => {
    let x = 0, z = 0;
    for (let k = 0; k < 40; k++) {
      const a = rng.range(0, Math.PI * 2), d = Math.sqrt(rng.range((dMin / arenaR) ** 2, (dMax / arenaR) ** 2)) * arenaR;
      x = Math.cos(a) * d; z = Math.sin(a) * d;
      if (!solidNear(props, x, z, pad)) break;
    }
    return [x, z];
  };
  // 1. lava fissures: long thin rectangles, never across the spawn; permanent once open
  const fissures: [number, number][] = [];
  for (let i = 0; i < C.first + C.later; i++) {
    let x = 0, z = 0, l = 0, rot = 0;
    for (let k = 0; k < 30; k++) {
      [x, z] = spot(22, arenaR - 12, 2.5);
      l = rng.range(F.l[0], F.l[1]); rot = rng.range(0, Math.PI);
      // both ends stay out of the spawn circle, and fissures keep apart from each other
      const ok = [-1, 1].every((s) => Math.hypot(x + Math.cos(rot) * l * s, z + Math.sin(rot) * l * s) > EMBER.spawnClear + 4)
        && fissures.every(([fx, fz]) => (fx - x) ** 2 + (fz - z) ** 2 > 18 ** 2);
      if (ok) break;
    }
    fissures.push([x, z]);
    const late = i >= C.first;
    out.push({ kind: 'burn', x, z, r: 0, l, w: rng.range(F.w[0], F.w[1]), rot, start: late ? F.laterStart + (i - C.first) * F.laterEvery * (F.later / C.later) : 0,
      warn: late ? F.warnLate : F.warn, active: Infinity, power: F.power, tag: 'fissure' });
  }
  // 2. eruption volleys: 12 groups that repeat every 12 x 40 s, so the sky keeps falling in endless runs too
  const period = C.groups * C.gap;
  for (let g = 0; g < C.groups; g++) {
    const [cx, cz] = spot(18, arenaR - 16, 0);
    const t0 = V.start + g * C.gap, pts: [number, number][] = [];
    for (let b = 0; b < V.bombs; b++) {
      let x = cx, z = cz;
      for (let k = 0; k < 20; k++) {
        const a = rng.range(0, Math.PI * 2), d = b === 0 ? 0 : rng.range(3, V.spread);
        x = cx + Math.cos(a) * d; z = cz + Math.sin(a) * d;
        const dd = Math.hypot(x, z);
        if (dd > EMBER.spawnClear && dd < arenaR - 6 && pts.every(([px, pz]) => (px - x) ** 2 + (pz - z) ** 2 > 4.5 ** 2)) break;
      }
      const dd = Math.hypot(x, z);
      if (dd <= EMBER.spawnClear || dd >= arenaR - 5) continue; // could not find a fair spot: skip this bomb
      pts.push([x, z]);
      const warn = V.warn + b * V.stagger;
      out.push({ kind: 'crush', x, z, r: V.r, start: t0, warn, active: 0, every: period, power: V.power, tag: 'bomb' });
      // the splash: a small burn pool that lights exactly when the bomb lands
      out.push({ kind: 'burn', x, z, r: V.pool.r, start: t0 + warn - 1, warn: 1, active: V.pool.active, every: period, power: V.pool.power, tag: 'pool' });
    }
  }
  // 3. sulphur vents: fixed steam vents that blow on their own rhythms
  const vents: [number, number][] = [];
  for (let i = 0; i < C.vents; i++) {
    let x = 0, z = 0;
    for (let k = 0; k < 30; k++) {
      [x, z] = spot(24, arenaR - 14, S.r);
      if (vents.every(([vx, vz]) => (vx - x) ** 2 + (vz - z) ** 2 > 30 ** 2) && fissures.every(([fx, fz]) => (fx - x) ** 2 + (fz - z) ** 2 > 12 ** 2)) break;
    }
    vents.push([x, z]);
    out.push({ kind: 'fog', x, z, r: S.r, start: S.start + i * 7 * (S.n / C.vents), warn: S.warn, active: S.active, every: rng.range(S.every[0], S.every[1]), power: S.power, tag: 'vent' });
  }
  return out;
}

export const MAP: MapCoreDef = {
  id: 'ember-wastes',
  name: 'Ember Wastes',
  tagline: 'A basalt plateau ringed by lava under an erupting sky. Fissures burn, bombs fall, vents blow steam.',
  order: 6,
  status: 'ready',
  time: { default: 'night', day: 'dusk' },
  unlock: { after: 'glacier-ruins', seconds: 600 },
  terrain: {
    // broad lava swells and shallow cooled-lava hollows (the renderer paints the hollows as glassy black flows)
    hills: { mounds: 14, dip: 0.34, rad: [14, 30], dipRad: [9, 17], amp: [0.8, 2.4], dipAmp: [0.5, 1.1], swell: 0.4, max: 2.8, min: -1.2 },
    props: [
      { kind: 'spire', count: 9, rMin: 26, inset: 6, size: [3.0, 4.6], clear: 10, h: [10, 17], hpPer: 4.5 },   // basalt fangs
      { kind: 'boulder', count: 24, rMin: 12, inset: 3, size: [1.5, 2.6], clear: 4, h: [0.8, 1.4], hpPer: 2 }, // column stumps
      { kind: 'tree', count: 34, rMin: 10, inset: 2, size: [0.5, 0.5], clear: 1.6, h: [1, 1], variants: DEAD_TREE_VARIANTS }, // charred trees
      { kind: 'rock', count: 55, rMin: 8, inset: 2, size: [0.5, 0.9], clear: 1, h: [0.8, 1.3] },               // obsidian shards
      { kind: 'bush', count: 50, rMin: 9, inset: 2, size: [1, 1], clear: 0, h: [1, 1], variants: ASH_BUSH_VARIANTS, layout: 'clumps' }, // ash-grass
    ],
  },
  fire: true,
  // only the dry ash-grass patches carry a grass fire; bare basalt never does (the charred trees and ash brush still burn)
  fireMask: ashGrassAt,
  nightPressure: 1.12,
  boss: { name: 'KILNHEART TITAN', hpMul: 1.2 },
  hazards: emberHazards,
};
